/* eslint-disable max-lines */
import qs from 'qs'
import { setParams } from '@/utils/url'
import { erpJointLoginGyl2, epaasJointLoginGyl, erpJointLoginGylFast } from '@/services/login'
import * as Sentry from '@sentry/react'
import { getLocalTerminalDomain } from '@/utils/terminal'
import { message as $message } from 'antd'

interface Host {
    hostInfo: {
        env: string
        isInErp: boolean
        isInEpaas: boolean
        isInGyl: boolean
    }
    isMaster: boolean
    isSubAppIndependence: boolean
}

export interface EpaasOpenParams {
    /**
     * @required
     * 服务key,
     **/
    serviceKey?: string
    /**
     * 页面 ID
     */
    pageId?: string

    /**
     * 其它参数，跳转时，会拼接到页面的 url 上
     */
    params?: object
}
interface Params {
    t?: string
    epaas?: string
    eTicket?: string
    f?: string
    isSubAppIndependence?: boolean
    isMaster?: boolean
    isProd: boolean
}
interface V3Option {
    isWindowOpen: boolean
    url: string
    tabUrl: string
    tabName: string
    serviceKey: string
    tabRefresh: boolean
    pageId?: string
    params?: Record<string, any> // epaas跳转时，会拼接到页面的 url 上
}
const ERP = 'erp'
const EPAAS = 'epaas'
const LOCAL = 'local'
const GYL = 'gyl'

// todo  这个是测试环境的url,
const proEpaasSdkUrl = 'https://src.erp321.com/epaas-jsapi/epaas_client.js'
const devEpaasSdkUrl = 'https://dev-src.jushuitan.com/epaas-jsapi/epaas_client.js'
export interface JumpOptions extends EpaasOpenParams {
    erpTabLink?: string /** 从 erp tab 打开的路径，默认值 gylOpenLink */
    erpTabParams?: object /** 从 erp tab 打开的属性, 参数是 jst_core.tab.open 的属性 */
    erpTabName: string /** erp tab 的标题 */
    erpTabRefresh?: boolean /** 如果已经有打开 erp tab，再次打开是否要刷新，默认值为 false */
    url: string
    isWindowOpen: boolean /** 是否新窗口打开 */
}
export const ERP_TAB = 'ERP_TAB'
export const BROWSER_TAB = 'BROWSER_TAB'
export const EPAAS_TAB = 'EPAAS_TAB'
export const GYL_TAB = 'GYL_TAB'
export const AppKeys = {
    Epaas: 'jst-epaas'
}
class HostEnv {
    private static instance: HostEnv
    private env = LOCAL
    private epaasSdk: any
    private isSubAppIndependence: boolean
    private isMaster: boolean
    static getInstance = (urlParams?: Params) => {
        if (!this.instance) {
            this.instance = new HostEnv(urlParams)
        }

        return this.instance
    }
    private constructor(urlParams?: Params) {
        const {
            eTicket, // eTicket 表示当前应用为 epaas 服务
            f, // 最早的平台约定（即将废弃）
            isSubAppIndependence,
            isMaster,
            epaas,
            token,
            t,
            container, // epaas 当前标记（即将废弃），值是 app_key
            _c // 最新的平台标记规范，值是 app_key
        } = urlParams || {}

        if (eTicket || f === EPAAS || epaas || container === AppKeys.Epaas || _c === AppKeys.Epaas) {
            this.env = EPAAS
        } else if (f === ERP) {
            this.env = ERP
        } else if (token) {
            this.env = GYL
        }

        this.isSubAppIndependence = !!isSubAppIndependence
        this.isMaster = !!isMaster
    }
    getEpaasSdk = () => this.epaasSdk
    setEpaasSdk = (sdk: any) => {
        this.epaasSdk = sdk
    }
    public getEnvInfo(): Host {
        return {
            hostInfo: {
                env: this.env,
                isInErp: this.env === ERP,
                isInEpaas: this.env === EPAAS,
                isInGyl: this.env === GYL
            },
            isSubAppIndependence: this.isSubAppIndependence,
            isMaster: this.isMaster
        }
    }
    public wrapPath = (path: string) => {
        const {
            hostInfo: { isInErp },
            isSubAppIndependence
        } = this.getEnvInfo()
        let url = path
        if (url.indexOf('http') !== 0) {
            if (isSubAppIndependence) {
                const domain = getLocalTerminalDomain()
                url = `${domain}${path}`
            } else {
                url = location.origin + path
            }
        }
        if (isInErp) {
            url = setParams(url, { f: ERP, m: 'n' }, false)
        }
        return url
    }

    public jumpV3(options: V3Option) {
        const { url, tabName, tabUrl, isWindowOpen, tabRefresh = true, serviceKey, pageId = '', params = {} } = options

        const {
            hostInfo: { isInErp, isInEpaas }
        } = this.getEnvInfo()

        let tabType = BROWSER_TAB
        if (isInEpaas) {
            tabType = EPAAS_TAB
        } else if (isInErp) {
            tabType = ERP_TAB
        }

        switch (tabType) {
            case EPAAS_TAB: {
                if (!serviceKey) {
                    this.epaasSdk?.service?.openErp({
                        tabName,
                        url: tabUrl,
                        isRefresh: tabRefresh
                    })
                } else {
                    this.epaasSdk?.service?.open({ pageId, serviceKey, params, m: 'n' })
                }
                break
            }
            case ERP_TAB: {
                openErpTab(tabName, tabUrl || this.wrapPath(url), tabRefresh)
                break
            }
            default: {
                const { isSubAppIndependence } = this.getEnvInfo()
                if (isWindowOpen || isSubAppIndependence) {
                    window.open(this.wrapPath(url), '_blank')
                } else {
                    history.pushState(null, url, url)
                }
                break
            }
        }
    }

    public jumpV2(erpParam: JumpOptions, epaasParams: EpaasOpenParams) {
        const { erpTabLink, erpTabName, erpTabRefresh = false, url, isWindowOpen } = erpParam || {}
        const { serviceKey } = epaasParams || {}
        const {
            hostInfo: { isInErp, isInEpaas }
        } = this.getEnvInfo()

        let tabType = BROWSER_TAB
        if (isInEpaas) {
            tabType = EPAAS_TAB
        } else if (isInErp && erpTabName) {
            tabType = ERP_TAB
        }

        switch (tabType) {
            case EPAAS_TAB: {
                if (erpTabLink) {
                    this.epaasSdk?.service?.openErp({
                        tabName: erpTabName,
                        url: erpTabLink,
                        isRefresh: erpTabRefresh
                    })
                } else {
                    this.epaasSdk?.service?.open(epaasParams)
                }
                break
            }
            case ERP_TAB: {
                openErpTab(erpTabName, erpTabLink || this.wrapPath(url), erpTabRefresh)
                break
            }
            default: {
                const { isSubAppIndependence } = this.getEnvInfo()
                if (isWindowOpen || isSubAppIndependence) {
                    window.open(this.wrapPath(url), '_blank')
                } else {
                    history.pushState(null, url, url)
                }
                break
            }
        }
    }
    /**
     * 建议使用jumpV2
     * */
    public jump(params: JumpOptions) {
        const {
            hostInfo: { isInErp, isInEpaas, isInGyl }
        } = this.getEnvInfo()
        let temp = BROWSER_TAB
        const { erpTabLink, erpTabParams, erpTabName, erpTabRefresh = false, url, serviceKey, isWindowOpen } = params

        if (isInEpaas && serviceKey) {
            temp = EPAAS_TAB
        } else if (isInErp && erpTabName) {
            temp = ERP_TAB
        } else if (isInGyl && url) {
            // 供应链iframe容器下的打开tab
            temp = GYL_TAB
        }
        console.warn('debug jump', params, isInErp, isInEpaas, isInGyl, temp)
        switch (temp) {
            case EPAAS_TAB: {
                if (erpTabParams) {
                    ;(window as any).jst_core.tab.open(erpTabParams)
                } else if (erpTabLink) {
                    this.epaasSdk?.service?.openErp({
                        tabName: erpTabName,
                        url: erpTabLink || this.wrapPath(url),
                        isRefresh: erpTabRefresh
                    })
                } else {
                    this.epaasSdk?.service?.open(params)
                }
                break
            }
            case ERP_TAB: {
                openErpTab(erpTabName, erpTabLink || this.wrapPath(url), erpTabRefresh)
                break
            }
            case GYL_TAB: {
                if (isWindowOpen) {
                    return window.open(this.wrapPath(url), '_blank')
                }
                // 供应链 iframe 容器下
                window.parent.postMessage(
                    {
                        eventName: 'ROUTE_JUMP',
                        eventData: url
                    },
                    '*'
                )
                break
            }
            default: {
                const { isSubAppIndependence } = this.getEnvInfo()
                if (isWindowOpen || isSubAppIndependence) {
                    window.open(this.wrapPath(url), '_blank')
                } else {
                    history.pushState(null, url, url)
                }
                break
            }
        }
    }
}

export const loadEpaasSDK = async (isProd: boolean) => {
    if (window?.epaas_jsapi) {
        return window?.epaas_jsapi?.connect()
    }
    const getSdk = () => {
        return new Promise((resolve, reject) => {
            let script = document.createElement('script')
            script.type = 'text/javascript'

            script.src = isProd ? proEpaasSdkUrl : devEpaasSdkUrl
            script.onerror = error => {
                reject(error)
            }
            script.onload = function () {
                resolve(window?.epaas_jsapi)
            }
            document.body.appendChild(script)
        })
    }
    const sdk: any = await getSdk()
    return sdk?.connect()
}

export const getInstance = (params?: Params) => {
    const urlParams = qs.parse(location.search, { ignoreQueryPrefix: true })

    const { eTicket, f, epaas } = urlParams || {}
    const instance = HostEnv.getInstance({ ...urlParams, ...params })

    if (instance.env === EPAAS) {
        try {
            ;(async () => {
                const epaasSdk = await loadEpaasSDK(params?.isProd)
                epaasSdk && instance.setEpaasSdk(epaasSdk)
            })()
            return new Promise(resolve => {
                resolve(instance)
            })
        } catch (error) {
            return new Promise((resolve, reject) => {
                // eslint-disable-next-line prefer-promise-reject-errors
                reject('环境初始化失败')
            })
        }
    }

    return new Promise(resolve => {
        resolve(instance)
    })
}

/**
 * 打开 ERP tab
 * @param url 如果是 erp 链接可以是相对路径，如果是打开供应链链接，需要通过 wrapPath 处理下
 * @param isRefresh 如果已经有打开 erp tab，再次打开是否要刷新，默认值为 false
 * */
export function openErpTab(name: string, url: string, isRefresh = false) {
    window.jst_core.tab.open({
        name, // : '订单管理',
        // erp项新开tab会解一次码，导致参数中的编码被解开，如有"#"等特殊字符会有问题
        url, // : 'https://c.jushuitan.com/app/order/order/list.aspx?queryUnshippedTime=12h',
        isRefresh
    })
}

/**
 * 短token换长token, 支持epaas和erp环境
 * */
export const exchangeToken = async (
    params: { eTicket: string; ffrom?: string } & { t: string; ffrom?: string; isRapid?: boolean; serviceKey?: string }
) => {
    const { eTicket, t, ffrom, isRapid = false, serviceKey = '' } = params

    const getTokenService = () => {
        if (eTicket) {
            return epaasJointLoginGyl({ eTicket, serviceKey, fromSource: ffrom })
        }

        if (isRapid) {
            return erpJointLoginGylFast({ token: t }, { fromSource: ffrom, isRapid: true })
        }

        return erpJointLoginGyl2({ token: t }, { fromSource: ffrom })
    }

    const { success, data, message } = await getTokenService()

    if (success) {
        return data
    } else {
        if (message) {
            $message.error(message)
        }
        Sentry.captureException(new Error(`换token异常, 参数t=${t}, eTicket=${eTicket}`))
    }
}

// 供应链token换erp  token
export const getErpTokenByGylToken = () => {
    // TODO 待提供接口
}
